/**
 * @file 手書きを画像にする (メモの手書きの PNG、ページと書き込みを重ねた1枚)。
 *
 * @what: 手書きの文書を白い紙の PNG にする。ページ画像と書き込みを重ねるときは、画像の元の大きさで
 *   出力し、書き込みは論理座標から画像の幅に合わせて拡大縮小する。
 * @why: AI 採点はメモの画像を読む。ページの書き込みをメモに写せれば、本に書いた解答をそのまま
 *   採点に出せる。
 * @failure: Canvas が使えなければ throw (呼び出し側が文言表示)。
 */

import { paintHandwriting, paintStroke, type HandwritingDoc } from "../model/handwriting";

/** メモの手書きを PNG にする倍率である (論理座標を2倍にして書き出す)。 */
const EXPORT_SCALE = 2;

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("png encode failed"))), "image/png");
  });
}

/** 手書きの文書を白い紙の PNG にする。 */
export async function exportHandwritingPng(doc: HandwritingDoc): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = doc.width * EXPORT_SCALE;
  canvas.height = doc.height * EXPORT_SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d unavailable");
  paintHandwriting(ctx, doc, EXPORT_SCALE);
  return toPng(canvas);
}

/** ページ画像 (object URL) の上に書き込みを重ねた1枚を PNG にする。 */
export async function composePageWithInk(imageUrl: string, doc: HandwritingDoc): Promise<Blob> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("image decode failed"));
    img.src = imageUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d unavailable");
  ctx.drawImage(img, 0, 0);
  // paintHandwriting は先に面を消すので使わず、線だけを重ねる (ページを消さない)。
  const scale = img.naturalWidth / doc.width;
  for (const stroke of doc.strokes) paintStroke(ctx, stroke, scale, true);
  return toPng(canvas);
}
